<template>
  <div class="generalInfo">
    <div :class="[{'row': showUpdatorInfo}]">
      <div
        class="generalInfo_left"
        :class="[{'small-6 medium-6 large-6 columns': showUpdatorInfo}]"
      >
        <template v-if="!isEdit && showPersonIncharge">
          <i
            v-if="isWaitingApprove"
            class="gray_font"
          >
            {{ $t('workPermit.waitingApproveBy', [personIncharge]) }}
          </i>
          <i
            v-else
            class="gray_font"
          >
            {{ $t('workPermit.underReviewBy', [personIncharge]) }}
          </i>
        </template>
        <div>
          <i
            v-if="!isEdit && hasSubmitInfo"
            class="gray_font"
          >
            {{ $t('workPermit.submitHint', [submitInfo.name, submitInfo.time]) }}
          </i>
        </div>
      </div>
      <div :class="['generalInfo_right', {'small-6 medium-6 large-6 columns': showUpdatorInfo}]">
        <div
          v-if="isProjectAvailable"
          class="suspend-resume-extend-btns"
          :class="[{'top-right-buttons': !showUpdatorInfo}]"
        >
          <div
            v-if="canApprovePP || canApproveWP || showEditBtn || showExportBtn"
            class="btns-group"
          >
            <HKLViewLayout
              :checkIsHKL="true"
            >
              <HKLButton
                color="orange"
                @click="generateNotice"
                v-if="showExportBtn"
              >
                {{$t('settings.security.workPermit.generateNotice')}}
              </HKLButton>
            </HKLViewLayout>
            <button
              v-if="showEditBtn && !isEdit"
              type="button"
              class="btn btn--darkblue"
              @click="goToCreate()"
            >
              {{ $t('action.edit') }}
            </button>
            <button
              v-if="showSuspendBtn"
              type="button"
              class="btn btn--darkblue"
              @click="openSuspendModal()"
            >
              {{ $t('action.suspend') }}
            </button>
            <button
              v-if="showResumeBtn"
              type="button"
              class="btn btn--orange"
              @click="openResumeModal()"
            >
              {{ $t('action.resume') }}
            </button>
            <button
              v-if="showExtBtn"
              type="button"
              class="btn btn--orange"
              @click="openExtendModal()"
            >
              {{ $t('action.extend') }}
            </button>
            <template v-if="showExtensionBtns">
              <HKLButton
                color="darkblue"
                action="rejectExtension"
                @click="openRejectExtensionModal"
              />
              <HKLButton
                color="orange"
                action="confirmExtension"
                @click="openConfirmExtendModal"
              />
            </template>
          </div>
          <div class="btns-group">
            <button
              v-if="showReassignBtn"
              class="btn btn--orange"
              @click="openReAssignModal()"
            >
              {{ $t('action.reAssign') }}
            </button>
            <button
              v-if="showRequestBtn"
              type="button"
              class="btn btn--orange"
              @click="openRequestExtendModal()"
            >
              {{ $t('action.requestExtension') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="general-info-main">
      <div class="general-info-basic-left">
        <div
          v-if="isStandalone && currentWorkPermit.isTenantProject"
          class="general-info__tenantTradeName"
        >
          <p class="bold">
            {{ $t('user.tenantName') }}
          </p>
          <p>{{ companyName }}</p>
        </div>
        <div class="generalInfo_contractorName new-multiselect">
          <p :class="['bold', {'required-flag': isContractorEditable}]">
            {{ $t('workPermit.contractorName') }}
          </p>
          <HKLSelect
            v-if="isContractorEditable"
            v-hkldinput
            :class="{'small-6 medium-6 large-6 columns': !showUpdatorInfo, 'error-highlight': !validContractor, 'general-info-contractor-select': true}"
            :value="contractorSelectValue"
            :options="companyHklOptions"
            :showSearch="true"
            :singleTaggableInput="true"
            placeholder=""
            @search="onContractorSearch"
            @input="onContractorInput"
            @change="updateContractor"
          />
          <p v-else>
            {{ contractorInfo && contractorInfo.name || $t('emptyValue') }}
          </p>
        </div>
        <StandaloneContacts
          v-if="isStandalone && isTenantProject"
          :isEdit="isEdit"
        />
        <div class="general-info__time">
          <div class="generalInfo_duration">
            <p :class="['bold', {'required-flag': isEdit}]">
              {{ $t('workPermit.durationOfWork') }}
            </p>
            <i
              v-if="isEdit"
              class="gray_font"
            >
              {{ $t('workPermit.plsInputWorkPeriod') }}
            </i>
            <div class="generalInfo_timePicker">
              <DateTimeSelector
                v-if="isEdit"
                :key="`${timestamp}_generalInfo_startDate`"
                :date="currentWorkPermit && currentWorkPermit.startDate"
                :showClearBtn="true"
                :setMinTime="true"
                :disabledDates="disabledDates"
                :highlightClass="checkIfInvalidClassByDate(validStartDate)"
                :required="true"
                :dirtyCheck="true"
                @updateDate="val => updateField('startDate', val)"
              />
              <span v-else>{{ formatDate( currentWorkPermit && currentWorkPermit.startDate, true) }}</span>
              <span
                v-if="isEdit"
                class="generalInfo_timePickerTo"
              >
                {{ $t('dateTo') }}
              </span>
              <span v-else>-</span>
              <DateTimeSelector
                v-if="isEdit"
                :key="`${timestamp}_generalInfo_endDate`"
                :date="currentWorkPermit.endDate"
                :showClearBtn="true"
                :setMinTime="true"
                :required="true"
                :highlightClass="checkIfInvalidClassByDate(validEndDate)"
                :disabledDates="disabledDates"
                :dirtyCheck="true"
                @updateDate="val => updateField('endDate', val)"
              />
              <span v-else>{{ formatDate(currentWorkPermit && currentWorkPermit.endDate, true) }}</span>
            </div>
          </div>
          <div v-if="currentWorkPermit.extendDate">
            <p class="bold">
              {{ $t('workPermit.originalDate') }}
            </p>
            <div class="generalInfo_timePicker">
              <span>{{ formatDate(currentWorkPermit.extendDate, true) }}</span>
            </div>
          </div>
          <div
            v-if="currentWorkPermit.requestExtendDate"
            class="yellow_font"
          >
            <div>
              <span>{{ extendText }}</span>
            </div>
          </div>
        </div>
        <div class="generalInfo_building">
          <p :class="['bold', {'required-flag': !isTenantProject && isEdit}]">
            {{ $t('workPermit.building') }}
          </p>
          <HKLSelect
            v-if="!isTenantProject && isEdit"
            :class="{'small-6 medium-6 large-6 columns': !showUpdatorInfo, 'error-highlight': !validBuilding}"
            :value="selectedBuildingCode || undefined"
            :options="buildingHklOptions"
            :showSearch="true"
            placeholder=""
            @change="onBuildingHklChange"
          />
          <p v-else>
            {{ selectedBuilding.fullName || '--' }}
          </p>
        </div>
        <div class="generalInfo_floor new-multiselect">
          <p :class="['bold', {'required-flag': isEdit}]">
            {{ $t('workPermit.floor') }}
          </p>
          <i
            v-if="isEdit"
            class="gray_font"
          >
            {{ $t('workPermit.plsInputInvolvedFloors') }}
          </i>
          <HKLMultiSelect
            v-if="isEdit"
            :class="{'small-6 medium-6 large-6 columns': !showUpdatorInfo, 'error-highlight': !validFloor, 'general-info-floor-select': true}"
            :value="selectedFloorLabels"
            :options="floorsHklOptions"
            :respectOptionDisabled="disableProtectedFloorRemoval"
            :showSearch="true"
            dropdownClassName="general-info-floor-dropdown"
            placeholder=""
            @change="onFloorsHklChange"
          />
          <p v-else>
            {{ getFloor() }}
          </p>
        </div>
        <div
          v-if="!currentWorkPermit.isTenantProject"
          class="generalInfo_location"
        >
          <p :class="['bold', {'required-flag': isEdit}]">
            {{ $t('workPermit.location') }}
          </p>
          <div
            v-if="isEdit"
            :class="[{'small-6 medium-6 large-6 columns': !showUpdatorInfo}]"
          >
            <TextField
              :required="isEdit && !currentWorkPermit.isTenantProject"
              :value="currentWorkPermit.location"
              @updateValue="val => updateLocation(val)"
            />
          </div>
          <p v-else>
            {{ locationValue }}
          </p>
        </div>
        <div
          v-if="currentWorkPermit.isTenantProject && currentWorkPermit.standalone"
          class="generalInfo_premises small-6 columns"
        >
          <label
            class="wp-premises-label"
          >
            {{ $t('tenancy.premises') }}
          </label>
          <div
            v-if="isEdit"
            class="wp-premises-edit"
          >
            <HKLInput
              class="wp-premises-input"
              :value="currentWorkPermit.wpPremises"
              :placeholder="$t('tender.general.premisesHint')"

              @input="$event => updateSAPremises($event.target.value)"
            />
          </div>
          <p v-else>
            {{ SAPremisesValue }}
          </p>
        </div>
        <div class="generalInfo_descriptionOfWork">
          <p :class="['bold', {'edit-label': isEdit, 'required-flag': isEdit}]">
            {{ $t('workPermit.descriptionOfWork') }}
          </p>
          <HKLSelect
            v-if="isEdit"
            :class="{'small-6 medium-6 large-6 columns': !showUpdatorInfo, 'error-highlight': !validWorkType}"
            :value="currentWorkPermit.workType || undefined"
            :options="workTypeHklOptions"
            :showSearch="true"
            placeholder=""
            @change="val => updateField('workType', val)"
          />
          <p v-else>
            {{ currentWorkPermit.workType }}
          </p>
          <div
            v-if="isTelecom"
            class="generalInfo_telecom small-6 medium-6 large-6"
            :class="{'readonly': !isEdit}"
          >
            <div class="row">
              <InputField
                class="generalInfo_telecom-item small-12 medium-4 large-4"
                :label="$t('workPermit.telecom.TBC_MDFRoom')"
                :showFlag="isEdit"
                :showColon="false"
                :required="true"
                :readonly="!isEdit"
                :value="currentWorkPermit.telecomTbcNbfRoom"
                @updateValue="val => updateField('telecomTbcNbfRoom', val)"
              />
              <InputField
                class="generalInfo_telecom-item small-12 medium-4 large-4"
                :label="$t('workPermit.telecom.telDuct')"
                :showFlag="isEdit"
                :showColon="false"
                :required="true"
                :readonly="!isEdit"
                :value="currentWorkPermit.telecomTelDuct"
                @updateValue="val => updateField('telecomTelDuct', val)"
              />
              <InputField
                class="generalInfo_telecom-item small-12 medium-4 large-4"
                :label="$t('workPermit.telecom.otherLocations')"
                :showFlag="false"
                :showColon="false"
                :required="false"
                :readonly="!isEdit"
                :value="currentWorkPermit.telecomOtherLocations"
                @updateValue="val => updateField('telecomOtherLocations', val)"
              />
            </div>
          </div>

          <div v-if="(isOthers || isEvent) && isEdit">
            <i :class="['gray_font', 'required-flag']">{{ isEvent ? $t('workPermit.descriptionEventRequireHint'): $t('workPermit.descriptionRequireHint') }}</i>
          </div>
          <div :class="{'small-6 medium-6 large-6 columns': !showUpdatorInfo}">
            <TextField
              v-hkldinput
              :readonly="!isEdit"
              :required="isOthers || isEvent"
              :value="currentWorkPermit.description"
              @updateValue="val => updateField('description', val)"
            />
          </div>
        </div>
        <div
          v-if="isEdit || noisyWorkingHoursChecked"
          class="generalInfo_noisyWorkingHours"
        >
          <div class="emphasis noisyWorkingHour-section">
            <input
              v-if="isEdit"
              id="workPermit.noisyWorkingHours"
              v-model="noisyWorkingHoursChecked"
              v-hkldinput
              type="checkbox"
              class="general-checkbox"
              name="noisyWorkingHours"
              @change="event => onNoisyWorkingHourChecked(event)"
            >
            <label
              class="bold"
              for="workPermit.noisyWorkingHours"
            >
              {{ $t('workPermit.noisyWorkingHours') }}
            </label>
          </div>
          <div
            v-if="noisyWorkingHoursChecked"
            :class="{'large-5': !isEdit}"
          >
            <WorkingHours
              ref="noisyWorkingHours"
              :hideLabel="true"
              :disabled="!isEdit"
              :isMandatory="noisyWorkingHoursChecked"
              :workingHours="currentWorkPermit.noisyWorkingHours || []"
              :updateValueByPath="updateFieldByPath"
              :fieldPath="['noisyWorkingHours']"
            />
          </div>
        </div>
        <div
          v-if="noisyWorkingHoursChecked"
          class="generalInfo_noisyWorkingHours"
        >
          <label class="bold">
            {{ $t('workPermit.noisyWorkingHoursRemarks') }}
          </label>
          <HKLTextarea
            v-if="isEdit"
            class="small-12 medium-6 large-6"
            :value="currentWorkPermit.noisyWorkingHoursRemarks"
            @onChange="value => updateField('noisyWorkingHoursRemarks', value)"
          />
          <HKLText
            v-else
            :text="currentWorkPermit.noisyWorkingHoursRemarks || $t('emptyValue')"
          />
        </div>
      </div>
      <HKLQRCode
        class="work-permit-qr-code"
        :class="{'qr-margin-top': hasBtnOnTop}"
        :text="qrCodeUrl"
        :hint="$t('hint.readQRHint', [qrCodeUrl])"
      />
    </div>
    <div
      class="generalInfo_attachment"
      :class="{'error-highlight' : !validAttachmentDoc}"
    >
      <p :class="['bold', {'required-flag': isAttachmentMandatory && isEdit}]">
        {{ $t('workPermit.attachment') }}
      </p>
      <i
        v-if="isEdit && attachmentHint"
        class="gray_font"
      >
        {{ attachmentHint }}
      </i>
      <HkldDropzone
        v-if="(isEdit && canExternalUpload) || canUploadUnderAppr"
        :sourceId="sourceId"
        :metaData="metaData"
      />
      <EditAttachments
        :sourceId="sourceId"
        :canDelete="(isEdit && canExternalUpload) || canUploadUnderAppr"
        :canDownloadAll="false"
        :canEdit="isEdit && canExternalUpload"
        :showEditPencil="showEditPencilFunc"
        :showAllDeleteBtn="false"
        :showDeleteBtnFunc="showDeleteBtnFunc"
        :getFilesList="getFilesList"
      />
      <div v-if="!isEdit && !filesLength">
        --
      </div>
    </div>
    <TimePickerModal
      ref="extendWorkPermitModal"
      v-model="extendDate"
      :disabledDateTimes="extendDisabledDate"
      :extendButton="$t('action.extend')"
      @cancelEvent="cancelExtend"
      @extendEvent="checkInsuranceDateThenHKLDExtendWorkpermit"
    />
    <TimePickerModal
      ref="requestExtendWorkPermitModal"
      v-model="requestExtendDate"
      :disabledDateTimes="extendDisabledDate"
      :extendButton="$t('action.request')"
      @cancelEvent="cancelRequestExtend"
      @extendEvent="checkInsuranceDateThenRequestExtension"
    />
    <TimePickerModal
      ref="confirmExtendWorkPermitModal"
      v-model="confirmExtendDate"
      :disabledDateTimes="extendDisabledDate"
      :extendButton="$t('action.confirm')"
      :extendHint="$t('workPermit.extendConfirmHint')"
      @cancelEvent="cancelConfirmExtend"
      @extendEvent="checkInsuranceDateThenConfirmExtension"
    />
    <InsuranceNotCoverModal
      ref="insuranceNotCoverModal"
      :hint="insuranceNotCoverHint"
      @cancelEvent="clickInsuranceCancel"
      @continueEvent="clickInsuranceContinue"
    />

    <Modal
      ref="suspendWorkPermitModal"
      :showCloseBtn="false"
      class="suspend__work-permit"
      :modalId="`suspend-wp-${+new Date()}`"
    >
      <div slot="content">
        <div class="suspend-permit__remarks">
          <TextField
            class="workpermit-content-slot__review-text-input"
            :label="$t('workPermit.remarks')"
            :showColon="false"
            :value="suspendComments"
            @updateValue="val => suspendComments = val"
          />
        </div>
        <div class="suspend-permit__dropzone">
          <p class="suspend-permit-hint">
            {{ $t('workPermit.suspendDocHint') }}
          </p>
          <HkldDropzone
            ref="suspendDropzone"
            :sourceId="randomId"
            :maxNumberOfFiles="20"
            :autoProcess="false"
          />
        </div>
        <EditAttachments
          ref="suspendAttachment"
          :sourceId="randomId"
          :getFilesList="getSuspendFileList"
          class="suspend-file_container"
          :initLoading="false"
          :isShowDes="false"
          :canEdit="false"
          :isShowUploadedDate="false"
          :canDelete="true"
        />
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--darkblue"
          @click="openCancelSuspendConfirmModal()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn--orange"
          @click="suspendPermit()"
        >
          {{ $t('action.suspend') }}
        </button>
      </div>
    </Modal>
    <Modal
      ref="resumeWorkPermitModal"
      :modalId="`resume-wp-${+new Date()}`"
    >
      <p class="font-bold">
        {{ $t('workPermit.resumeHint') }}
      </p>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--darkblue"
          @click="closeResume()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn--orange"
          @click="resumePermit()"
        >
          {{ $t('action.yes') }}
        </button>
      </div>
    </Modal>
    <WorkPermNextReviewerModal
      :ref="`reAssign_nextReivewerModal_${currentWorkPermit.id}`"
      :modalId="`reAssign_nextReivewerModal_${currentWorkPermit.id}-${+new Date()}`"
      :excludeCurrent="true"
      @multi-select-submited="reAssignReviewer"
    >
      <div slot="wp-next-reviewer-content">
        <p class="emphasis">
          {{ $t('workPermit.reAssign') }}
        </p>
      </div>
      <ReassignHistories slot="additional-content" />
    </WorkPermNextReviewerModal>
    <ActionConfirmModal
      ref="cancelSuspendConfrimModal"
      :confirmTitle="$t('workPermit.removeDocsHint')"
      :modalId="`cancel-suspend-${+new Date()}`"
    />
    <ActionConfirmModal
      ref="extensionConflictModal"
      :modalId="`extension-conflict-${+new Date()}`"
      :confirmTitle="$t('standaloneWorkPermit.conflictWithSWP')"
    />
    <RejectExtensionModal ref="rejectExtensionModal" />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { getCurrentInstance, unref, computed } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import HKLButton from '@/controls/Button/HKLButton'
import DateTimeSelector from '@/controls/DateTimeSelector'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import InputField from '@/controls/InputField'
import InsuranceNotCoverModal from '@/controls/InsuranceNotCover'
import Modal from '@/controls/Modal'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import WorkingHours from '@/controls/WorkingHours'
import TextField from '@/controls/TextField'
import TimePickerModal from '@/controls/TimePickerModal'
import HKLQRCode from '@/controls/HKLQRCode'
import * as companiesApi from '@/api/companies/request'
import * as insuranceAPI from '@/api/insurance/request'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import { companyType } from '@/utils/locale'
import { tmsPermsTypes, aclHelper } from '@/utils/aclHelper'
import { EmailTypes, UserDomain, workingHourAnytime, workingDayPublicHoliday } from '@/utils/consts'
import { formatFloors, getLastHistoryByStatusWithoutResume } from '@/api/workPermit/util'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getFloorIndex, getBuildingFloorZoneMap } from '@/api/buildings/request'
import { getKey } from '@/api/common/request'
import { getPurchase } from '@/api/projects/util'
import { getWPConflictStatus } from '@/api/workPermit/request'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
import HKLText from '@/controls/HKLText.vue'
import HKLInput from '@/controls/Input/HKLInput'
import { getActiveAndFutureFloors } from '../ccd/tenancy/utils'
import {
  ALL_FLOORS,
  getSAWorkTypeListMap,
  WORK_PERMIT_EXTENDED,
  WORK_PERMIT_STATUS,
  WORK_PERMIT_TYPES,
  getWorkTypeMap,
  getWorkTypeListMap,
  WP_ACTION_TYPE,
  WP_PROJECT_TYPE_MAP,
  WP_ACTION_TYPE_BY_NEXT_STATUS_MAPPING,
  WP_ACTION_ALLOWED_STATUS,
} from './consts'
import {
  calcYesterday,
  canProcessCurrentWP,
  getParsedWorkPermitHistoryActor,
  getWorkPermitHistoryCreatedAt,
  isWorkPermitEditable,
  disableTenantContractorEdit,
} from './utils'
import RejectExtensionModal from './RejectExtensionModal'
import StandaloneContacts from './StandaloneContacts'
import ReassignHistories from './ReassignHistories'
import WorkPermNextReviewerModal from './WorkPermNextReviewerModal'
import useWpNextStatus from './hooks/useWpNextStatus'
import { useStoreGetters } from '@/store'
import { exportSAPermit, exportT3Permit } from '@/api/printers/request'
import HKLViewLayout from '@/controls/Layout/HKLViewLayout.vue'
import { getPhysicalSite } from '@/utils/siteUtils'
import { getSubsidiaryTextArray } from '@/api/postman/util'
import filters from '@/filters/filters'

export default {
  name: 'GeneralInfo',
  components: {
    HKLButton,
    HKLInput,
    RejectExtensionModal,
    InsuranceNotCoverModal,
    TimePickerModal,
    ActionConfirmModal,
    DateTimeSelector,
    HKLSelect,
    HKLMultiSelect,
    WorkingHours,
    Modal,
    TextField,
    InputField,
    HKLTextarea,
    HKLText,
    EditAttachments,
    HkldDropzone,
    WorkPermNextReviewerModal,
    ReassignHistories,
    HKLQRCode,
    StandaloneContacts,
    HKLViewLayout,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: true,
    },
    isStandalone: {
      type: Boolean,
      default: false,
    },
    isProjectAvailable: {
      type: Boolean,
      default: false,
    },
    disableProtectedFloorRemoval: {
      type: Boolean,
      default: true,
    },
  },
  setup (props) {
    let returnData = {}
    const { proxy } = getCurrentInstance()
    const canProcess = (permType) => proxy.isProjectAvailable && canProcessCurrentWP(permType)
    const { currentStatusRef } = useWpNextStatus(canProcess)
    returnData = {
      ...returnData,
      currentStatusRef,
    }
    const { currentTenantProjectRef } = useStoreGetters('tenantProject', ['currentTenantProject'])
    const { currentWorkPermitRef } = useStoreGetters('workPermit', ['currentWorkPermit'])
    const generateNotice = async () => {
      const currentTP = unref(currentTenantProjectRef)
      const currentWp = unref(currentWorkPermitRef)
      const tenancy = currentWp.tenancy
      const duration = `${dateUtil.formatDate(currentWp.startDate, true)} - ${dateUtil.formatDate(currentWp.endDate, true)}`
      const { BuildingFloorZoneMap } = await getBuildingFloorZoneMap()
      const building = BuildingFloorZoneMap[lodash.get(tenancy, 'building.code')]
      const site = getPhysicalSite(
        lodash.get(currentTP, 'tenancy.building.sites') || building.sites,
      )
      const subsidiaryArray = getSubsidiaryTextArray(site.subsidiary)
      const noisyWorkingHours = currentWp.noisyWorkingHours
        .map((noisyWorkingHour) => ({
          workingDayFrom: filters.customWorkingDayLabel(noisyWorkingHour.workingDayFrom),
          workingDayTo: noisyWorkingHour.workingDayFrom === workingDayPublicHoliday ? '' : filters.customWorkingDayLabel(noisyWorkingHour.workingDayTo),
          workingHourFrom: noisyWorkingHour.workingHourFrom === workingHourAnytime ? filters.customWorkingHourLabel(noisyWorkingHour.workingHourFrom) : noisyWorkingHour.workingHourFrom,
          workingHourTo: noisyWorkingHour.workingHourFrom === workingHourAnytime ? '' : noisyWorkingHour.workingHourTo,
        }))
      const noisyWorkingHoursRemarks = currentWp.noisyWorkingHoursRemarks || i18n.t('emptyValue')
      if (currentWp.standalone) {
        await exportSAPermit({
          subsidiaryArray,
          code: currentWp.code,
          date: dateUtil.format(new Date(), 'DD MMM YYYY'),
          premises: proxy.SAPremisesValue || i18n.t('emptyValue'),
          building: proxy.selectedBuilding.fullName,
          contractorName: currentWp.contractor.name,
          floor: proxy.getFloor(),
          contactList: (currentWp.contactList || [])
            .map((contact) => Object.assign({}, contact, {
              phone: contact.phone || i18n.t('emptyValue'),
              tel: contact.tel || i18n.t('emptyValue'),
            })),
          workers: currentWp.workers
            .map((worker) => Object.assign({}, worker, {
              cardExpiryDate: worker.cardExpiryDate ? dateUtil.formatDate(worker.cardExpiryDate, false) : worker.cardExpiryDate,
              phone: worker.phone || '',
            })),
          company: tenancy.company || currentWp.tenancy.company,
          workType: currentWp.workType,
          workDesc: currentWp.description,
          isTelecom: proxy.isTelecom,
          telecomTbcNbfRoom: currentWp.telecomTbcNbfRoom,
          telecomTelDuct: currentWp.telecomTelDuct,
          telecomOtherLocations: currentWp.telecomOtherLocations,
          duration,
          noisyWorkingHours,
          noisyWorkingHoursRemarks,
        })
      } else if (currentWp.type === WORK_PERMIT_TYPES.WORK) {
        await exportT3Permit({
          subsidiaryArray,
          code: currentWp.code,
          date: dateUtil.format(new Date(), 'DD MMM YYYY'),
          contractorName: currentWp.contractor.name,
          duration,
          building: proxy.selectedBuilding.fullName,
          floor: proxy.getFloor(),
          workers: currentWp.workers
            .map((worker) => Object.assign({}, worker, {
              cardExpiryDate: worker.cardExpiryDate ? dateUtil.formatDate(worker.cardExpiryDate, false) : worker.cardExpiryDate,
              phone: worker.phone || '',
            })),
          workType: currentWp.workType,
          workDesc: currentWp.description,
          premises: currentTP.projectLocation || i18n.t('emptyValue'),
          noisyWorkingHours,
          noisyWorkingHoursRemarks,
        })
      }
    }
    const showExportBtn = computed(() => {
      const currentWp = unref(currentWorkPermitRef)
      if (
        // T3 WP
        (currentWp.isTenantProject && proxy.isWP && !currentWp.standalone) ||
        // T3 Standalone WP
        (currentWp.isTenantProject && proxy.isWP && currentWp.standalone)
      ) {
        return [
          WORK_PERMIT_STATUS.APPR,
          WORK_PERMIT_STATUS.INPROGRESS,
        ].includes(currentWp.status)
      }
      return false
    })
    returnData = {
      ...returnData,
      generateNotice,
      showExportBtn,
    }
    return returnData
  },
  data () {
    // assign suspend uploaded document's sourceId to randomId first
    const randomId = '00000000000' + new Date().getTime()

    return {
      currentSuspendDocs: [],
      timestamp: new Date(),
      yesterday: new Date((new Date()).getTime() - dateUtil.ONE_DAY_MILLI),
      validStartDate: true,
      validEndDate: true,
      validAttachmentDoc: true,
      validContractor: true,
      validFloor: true,
      validBuilding: true,
      validWorkType: true,
      validDescription: true,
      NORMAL_WORK_TYPE_LIST: getWorkTypeListMap(),
      STANDALONE_WORK_TYPE_LIST: getSAWorkTypeListMap(),
      filesLength: 0,
      WORK_PERMIT_STATUS,
      searchCompaniesResult: [],
      contractorSearchText: '',
      suspendComments: '',
      extendDate: null,
      requestExtendDate: null,
      confirmExtendDate: null,
      suspendEffectiveDate: null,
      randomId,
      checkInsuranceCancel: () => {},
      checkInsuranceContinue: () => {},
      insuranceNotCoverHint: null,
      statusCanSuspend: [WORK_PERMIT_STATUS.WAPPR, WORK_PERMIT_STATUS.ACKNOWLEDGED, WORK_PERMIT_STATUS.REVIEWED, WORK_PERMIT_STATUS.REVIEWED2, WORK_PERMIT_STATUS.APPR, WORK_PERMIT_STATUS.REJECTED],
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'parentPermit', 'buildingFloorZoneMap', 'workPermitsInProject']),
    ...mapGetters('tenantProject', ['currentTenantProject', 'isWithdrawn']),
    ...mapGetters('projects', ['currentProject']),
    ...mapGetters('user', ['currentUser', 'isEngineer', 'isContractor', 'isContractorAdmin']),
    ...mapGetters('projectContractors', ['isMainUser', 'isSubUser', 'currentMainContractor', 'myContractor', 'isCurrentVendorAccount']),
    ...mapGetters('regions', ['backEndRegionalEnv']),
    isWP () {
      return this.currentWorkPermit.type === WORK_PERMIT_TYPES.WORK
    },
    canReassignToReviewer () {
      return aclHelper.getAccess(tmsPermsTypes.workpermit.reassignReviewer)
    },
    sourceId () {
      return lodash.get(this, 'currentWorkPermit.id', '')
    },
    qrCodeUrl () {
      return lodash.get(this.backEndRegionalEnv, 'urls.workPermitQRLink', '')
    },
    companyName () {
      return lodash.get(this.currentWorkPermit, 'tenancy.company.name', '')
    },
    extendText () {
      const requestExtendDate = this.formatDate(this.currentWorkPermit.requestExtendDate, true)
      const requestExtendText = this.isMainUser ? 'workPermit.requestExtend.mainUserExtendText' : 'workPermit.requestExtend.hkLandUserExtendText'
      return '(' + this.$i18n.t(requestExtendText) + requestExtendDate + ')'
    },
    showEditBtn () {
      if (this.currentWorkPermit.standalone) {
        if (this.currentWorkPermit.status === WORK_PERMIT_STATUS.REJECTED) {
          return this.isVendorHasStandaloneAccess || this.isTenantHasStandaloneAccess
        }
        return false
      } else {
        return this.isProjectAvailable && this.currentWorkPermit.status !== WORK_PERMIT_STATUS.CANCELLED && this.isCurrentPermitEditable &&
          (this.showEditBtnForMainContractor || this.showEditBtnForSubContractor) && this.canEditWp
      }
    },
    isTenantHasStandaloneAccess () {
      if (!this.currentWorkPermit.standalone || !this.isTenantProject) return false

      const tenancyContractors = lodash.get(this.currentWorkPermit, 'tenancy.tenancyContractors', [])
      const isTenancyContractor = tenancyContractors.some(item =>
        item.userId === this.currentUser.id && item.companyId === this.currentWorkPermit.contractorId)

      return isTenancyContractor || this.isEntitledTenancyAccount
    },
    isEntitledTenancyAccount () {
      if (!this.currentWorkPermit.standalone || !this.isTenantProject) return false

      const accounts = lodash.get(this.currentWorkPermit, 'tenancy.accounts', [])
      return accounts.some(item =>
        item.id === this.currentUser.id && lodash.get(item, 'TenancyAccount.perms.StandaloneWorkPermits', false))
    },
    isVendorHasStandaloneAccess () {
      return this.currentWorkPermit.standalone && !this.isTenantProject && this.isContractor && lodash.get(this.currentUser, 'perms.StandaloneWorkPermits', false)
    },
    canEditWp () {
      return (this.currentWorkPermit.contractorId === this.myContractor.companyId && (!disableTenantContractorEdit() || this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT)) || (this.isCurrentVendorAccount && this.currentWorkPermit.contractorId === this.currentUser.externalId)
    },
    isCurrentPermitEditable () {
      // TODO: replace !this.isWithdrawn with this.isProjectAvailable - Rango_Yuan@epam.com
      return !this.isWithdrawn && isWorkPermitEditable(this.currentWorkPermit)
    },
    showEditBtnForSubContractor () {
      return this.isSubUser && this.currentWorkPermit.type !== WORK_PERMIT_TYPES.PROJECT
    },
    showEditBtnForMainContractor () {
      return this.isMainContractor && (this.isPPAllUnavailable || this.isCMSUser)
    },
    isMainContractor () {
      return this.isMainUser || this.isCurrentVendorAccount
    },
    isCMSUser () {
      return this.currentUser.domain === UserDomain.CMS
    },
    showReassignBtn () {
      if (!this.isProjectAvailable) return false
      const isValidateStatus = WP_ACTION_ALLOWED_STATUS.includes(this.currentWorkPermit.status)
      if (!(this.isEngineer && isValidateStatus)) return false
      let reAssignPersonId
      switch (this.currentWorkPermit.status) {
        case WORK_PERMIT_STATUS.ACKNOWLEDGED:
          reAssignPersonId = lodash.get(this.currentWorkPermit, 'managerId')
          break
        case WORK_PERMIT_STATUS.REVIEWED:
        case WORK_PERMIT_STATUS.REVIEWED2:
          const histories = lodash.orderBy(this.histories, ['createdAt'], ['desc'])
          const statusHistory = histories.find(history => history.status === this.currentWorkPermit.status)
          reAssignPersonId = lodash.get(statusHistory, 'who.id')
          break
      }
      return this.canReassignToReviewer || this.currentUser.id === reAssignPersonId
    },
    isPPAllUnavailable () {
      return this.workPermitsInProject.filter(wp => {
        return wp.self.type === WORK_PERMIT_TYPES.PROJECT
      }).every(wp => wp.self.status === WORK_PERMIT_STATUS.REJECTED || wp.self.status === WORK_PERMIT_STATUS.CANCELLED)
    },
    showPersonIncharge () {
      return this.personIncharge && WP_ACTION_ALLOWED_STATUS.some(status => status === this.currentWorkPermit.status) && this.isEngineer
    },
    isParentPermitSuspend () {
      return this.currentWorkPermit.parentId && this.parentPermit && this.parentPermit.status === WORK_PERMIT_STATUS.SUSPENDED
    },
    isWaitingApprove () {
      const isWP = this.currentWorkPermit.type === WORK_PERMIT_TYPES.WORK
      return isWP ? this.currentWorkPermit.status === WORK_PERMIT_STATUS.REVIEWED2 : this.currentWorkPermit.status === WORK_PERMIT_STATUS.REVIEWED
    },
    personIncharge () {
      return lodash.get(this.currentWorkPermit, 'manager.displayName', null)
    },
    // I guess there may be something wrong. Because the selectedBuilding is assign to a         MultiSelect component's value(Line 132)
    selectedBuilding () {
      if (!this.isTenantProject && this.isStandalone && !this.isEdit) return this.currentWorkPermit.building

      return !this.isTenantProject
        ? this.buildingOptions.find(item => item.id === this.currentWorkPermit.buildingId) || {}
        : this.buildingFloorZoneMap[this.currentBuilding] || {}
    },
    project () {
      if (this.isStandalone) return {}

      if (this.isTenantProject) {
        return lodash.get(this, 'currentTenantProject.tenancy')
          ? lodash.get(this, 'currentTenantProject')
          : lodash.get(this.currentWorkPermit, 'project', {})
      } else {
        const hklProject = lodash.get(this, 'currentProject')
        return Object.keys(hklProject).length
          ? hklProject
          : lodash.get(this.currentWorkPermit, 'project', {})
      }
    },
    purchase () {
      return getPurchase(this.project)
    },
    buildingOptions () {
      if (this.isTenantProject) return []
      let buildings = []
      if (this.isStandalone) {
        buildings = lodash.values(this.buildingFloorZoneMap)
      } else {
        buildings = lodash.get(this.project, 'buildings', [])
        buildings = buildings.length > 0 ? buildings : lodash.get(this.project, 'site.buildings', [])
      }
      return buildings.map(item => ({
        id: item.id,
        code: item.code,
        floor: item.floor,
        commonFloor: item.commonFloor,
        fullName: item.fullName,
      }))
    },
    floorsOptions () {
      const selectedBuildingFloors = lodash.get(this.selectedBuilding, 'floor', [])
      if (this.isTenantProject) {
        const currentBuildingCommonFloor = this.selectedBuilding.commonFloor || []

        let tenantFloors = []
        if (this.isStandalone) {
          tenantFloors = getActiveAndFutureFloors(this.currentWorkPermit.tenancy)
        } else {
          tenantFloors = lodash.get(this.currentWorkPermit, 'project.floors', [])
        }

        const selectedFloorsOptions = [
          {
            title: i18n.t('workPermit.tenantFloorsTitle'),
            floors: lodash.orderBy(
              tenantFloors,
              floor => getFloorIndex(this.selectedBuilding.building, floor),
            ).map(floor => {
              return {label: floor}
            }),
          },
          {
            title: i18n.t('workPermit.commonFacilitiesFloorsTitle'),
            floors: lodash.orderBy(
              currentBuildingCommonFloor.filter(floor => !tenantFloors.includes(floor)),
              floor => getFloorIndex(this.selectedBuilding.building, floor),
            ).map(floor => {
              return {label: floor}
            }),
          },
        ]
        return selectedFloorsOptions
      } else {
        const allFloorArray = [ALL_FLOORS]
        if (this.currentWorkPermit.floors && this.currentWorkPermit.floors.length !== 0) {
          return (this.currentWorkPermit.isAllFloors ? allFloorArray : selectedBuildingFloors).map(floor => {
            return {label: floor}
          })
        } else if (selectedBuildingFloors.length === 0) {
          return []
        } else {
          return allFloorArray.concat(selectedBuildingFloors).map(floor => {
            return {label: floor}
          })
        }
      }
    },
    submitInfo () {
      let info = {}
      const submitHistory = getLastHistoryByStatusWithoutResume(this.currentWorkPermit.histories, WORK_PERMIT_STATUS.WAPPR)
      const actor = getParsedWorkPermitHistoryActor(submitHistory)
      const createdAt = getWorkPermitHistoryCreatedAt(submitHistory)
      if (actor && actor.displayName && createdAt) {
        info.name = actor.displayName
        info.time = dateUtil.formatDate(createdAt, true)
      }
      return info
    },
    hasSubmitInfo () {
      return !!(this.submitInfo.name && this.submitInfo.time)
    },
    noisyWorkingHoursChecked () {
      return this.currentWorkPermit.noisyWorkingHoursChecked
    },
    workTypeList () {
      return getWorkTypeMap() || []
    },
    isTelecom () {
      return this.currentWorkPermit.workType === this.workTypeList.TELECOM
    },
    isOthers () {
      return this.currentWorkPermit.workType === this.workTypeList.OTHERS
    },
    isEvent () {
      return this.currentWorkPermit.workType === this.workTypeList.EVENT
    },
    isEventAttachmentMandatoryEnabled () {
      return !!lodash.get(this.backEndRegionalEnv, 'workPermit.sawpDescWorkEventMandatory')
    },
    isAttachmentMandatory () {
      if (this.isTelecom) return true

      return this.isEvent && this.isEventAttachmentMandatoryEnabled
    },
    attachmentHint () {
      if (!this.currentWorkPermit.standalone) {
        return this.$t('workPermit.forElectrialHint')
      }

      if (this.isEvent) return this.$t('standaloneWorkPermit.eventWorkHint')
      if (this.isTelecom) return this.$t('standaloneWorkPermit.telecomWorkHint')

      return ''
    },
    lastSuspendHistory () {
      return this.histories.find(history => history.status === WORK_PERMIT_STATUS.SUSPENDED)
    },
    supsendAndResumehistory () {
      const historiesLen = this.histories.length
      const histories = this.histories

      return histories.sort(dateUtil.dateSortFunc('createdAt')).reduce((result, history, index) => {
        if (history.status === WORK_PERMIT_STATUS.SUSPENDED) {
          result.push(history)
          const resumeIndex = index + 1
          if (resumeIndex <= historiesLen - 1) {
            result.push(this.histories[resumeIndex])
          }
        }
        return result
      }, []).reverse()
    },
    suspendHistoryList () {
      return lodash.sortBy(this.histories.filter(item => item.status === WORK_PERMIT_STATUS.SUSPENDED), ['createdAt']).reverse()
    },
    extendedHistoryList () {
      return lodash.sortBy(this.histories.filter(item => item.detail.comments && item.detail.comments === WORK_PERMIT_EXTENDED), ['createdAt']).reverse()
    },
    lastResumeHistory () {
      const index = this.histories.findIndex(history => history.status === WORK_PERMIT_STATUS.SUSPENDED)
      if (index > 0) {
        return this.histories[index - 1]
      }
      return null
    },
    histories () {
      return lodash.orderBy(this.currentWorkPermit.histories, 'createdAt').reverse()
    },
    showUpdatorInfo () {
      return (this.currentWorkPermit.status !== WORK_PERMIT_STATUS.SUSPENDED && this.lastResumeHistory) ||
        (lodash.concat(this.suspendHistoryList).length > 0)
    },
    hasBtnOnTop () {
      return this.showBtnForContractor || this.showBtnForEngineer || this.showExportBtn || this.showSuspendBtn || this.showResumeBtn
    },
    showBtnForContractor () {
      return this.showEditBtn || this.showRequestBtn
    },
    showBtnForEngineer () {
      const canApprove = this.canApprovePP || this.canApproveWP
      const actionAfterAppr = canApprove && (this.showSuspendBtn || this.showResumeBtn || this.showExtBtn || this.showExtensionBtns)
      return actionAfterAppr || this.showReassignBtn
    },
    canSuspendByPerm () {
      const t = this.currentWorkPermit.type
      if (t === WORK_PERMIT_TYPES.PROJECT) return this.canApprovePP
      if (t === WORK_PERMIT_TYPES.WORK) return this.canApproveWP
      return false
    },
    showSuspendBtn () {
      return this.currentWorkPermit.status === WORK_PERMIT_STATUS.APPR && this.canSuspendByPerm
    },
    showResumeBtn () {
      return this.currentWorkPermit.status === WORK_PERMIT_STATUS.SUSPENDED &&
        !this.isParentPermitSuspend &&
        this.canSuspendByPerm
    },
    canApprovePP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approvePP) && this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT
    },
    canApproveWP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approveWP) && this.currentWorkPermit.type === WORK_PERMIT_TYPES.WORK
    },
    canUploadUnderAppr () {
      if (this.currentWorkPermit.status !== WORK_PERMIT_STATUS.APPR) return false
      return this.canHKLUserUpload || this.canExternalUpload
    },
    canHKLUserUpload () {
      return this.isProjectAvailable && (this.canUploadUnderPP || this.canUploadUnderWP)
    },
    canUploadUnderPP () {
      return this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT && (
        canProcessCurrentWP(tmsPermsTypes.workpermit.reviewPP) ||
        canProcessCurrentWP(tmsPermsTypes.workpermit.secondReviewPP))
    },
    canUploadUnderWP () {
      return this.currentWorkPermit.type === WORK_PERMIT_TYPES.WORK && (
        canProcessCurrentWP(tmsPermsTypes.workpermit.reviewWP) ||
        canProcessCurrentWP(tmsPermsTypes.workpermit.approveWP))
    },
    canExternalUpload () {
      if (this.currentWorkPermit.standalone) {
        return this.isVendorHasStandaloneAccess || this.isTenantHasStandaloneAccess
      } else {
        return this.isProjectAvailable && !this.isWithdrawn && this.canEditWp &&
          (this.isMainContractor || this.showEditBtnForSubContractor)
      }
    },
    contractorInfo () {
      return lodash.get(this, 'currentWorkPermit.contractor')
    },
    extendable () {
      return this.isProjectAvailable &&
      this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT && (
        this.currentWorkPermit.status === WORK_PERMIT_STATUS.APPR ||
        this.currentWorkPermit.status === WORK_PERMIT_STATUS.ENDED
      )
    },
    showRequestBtn () {
      if (!this.isProjectAvailable) return false
      return this.extendable && !this.isWithdrawn && this.isMainUser
    },
    showExtBtn () {
      return this.extendable &&
        this.canSuspendByPerm &&
        !this.currentWorkPermit.requestExtendDate
    },
    showExtensionBtns () {
      return this.extendable &&
        this.canSuspendByPerm &&
        this.currentWorkPermit.requestExtendDate
    },
    disabledDates () {
      const disableDate = {
        to: this.yesterday,
      }
      // just compare with wp duration
      const wpStartDate = lodash.get(this.parentPermit, 'startDate', '')
      const wpEndDate = lodash.get(this.parentPermit, 'endDate', '')
      if (wpStartDate) {
        disableDate.to = calcYesterday(new Date(wpStartDate))
        const wpStartTime = (new Date(wpStartDate)).getTime()
        const nowTime = (new Date()).getTime()
        if (nowTime > wpStartTime) {
          disableDate.to = this.yesterday
        }
      } else {
        disableDate.to = this.yesterday
      }
      if (wpEndDate) {
        disableDate.from = new Date(wpEndDate)
      }
      return disableDate
    },
    extendDisabledDate () {
      return {
        to: new Date(Math.max(new Date(this.currentWorkPermit.endDate || 0), new Date())),
      }
    },
    isTenantProject () {
      return this.currentWorkPermit.isTenantProject
    },
    isContractorEditable () {
      return this.isEdit && this.isTenantProject && this.isStandalone && this.isEntitledTenancyAccount
    },
    currentBuilding () {
      if (!this.isTenantProject) {
        return lodash.get(this.selectedBuilding, 'code')
      }
      return this.isStandalone ? lodash.get(this.currentWorkPermit, 'tenancy.building.code') : lodash.get(this.project, 'tenancy.building.code')
    },
    locationValue () {
      const value = this.currentWorkPermit.isTenantProject
        ? this.currentTenantProject.projectLocation
        : this.currentWorkPermit.location
      return value || this.$i18n.t('emptyValue')
    },
    floorsValue () {
      return (this.currentWorkPermit.isAllFloors ? [ALL_FLOORS] : this.currentWorkPermit.floors).map(floor => {
        let removeDisabled = this.currentWorkPermit.isTenantProject &&
          this.currentWorkPermit.type === WORK_PERMIT_TYPES.PROJECT &&
          this.currentWorkPermit.project.type === WP_PROJECT_TYPE_MAP.Fitout &&
          this.currentWorkPermit.project.floors.indexOf(floor) > -1
        return {label: floor, removeDisabled}
      })
    },
    protectedFloorLabels () {
      return this.floorsValue
        .filter(({ removeDisabled }) => removeDisabled)
        .map(({ label }) => label)
    },
    SAPremisesValue () {
      return this.currentWorkPermit.wpPremises || this.$i18n.t('emptyValue')
    },
    descriptionOptionValue () {
      let SAOptionList = lodash.cloneDeep(this.STANDALONE_WORK_TYPE_LIST) || []
      SAOptionList.push(this.workTypeList.OTHERS)
      return this.isStandalone ? SAOptionList : this.NORMAL_WORK_TYPE_LIST
    },
    companyOptions () {
      const searchResult = this.searchCompaniesResult.map((company) => company.name)
      const defaultResult = this.defaultCompanyOptions.map((company) => company.name)
      return lodash.uniq(searchResult.concat(defaultResult))
    },
    contractorSelectValue () {
      const name = lodash.get(this.contractorInfo, 'name')
      return name || null
    },
    companyHklOptions () {
      const options = (this.companyOptions || []).map((name) => ({ name, value: name }))
      const temporaryContractorOption = this.getTemporaryContractorOption()
      return temporaryContractorOption ? [temporaryContractorOption, ...options] : options
    },
    selectedBuildingCode () {
      return lodash.get(this.selectedBuilding, 'code') || null
    },
    buildingHklOptions () {
      return (this.buildingOptions || []).map((b) => ({
        name: b.fullName,
        value: b.code,
      }))
    },
    selectedFloorLabels () {
      if (this.currentWorkPermit.isAllFloors) {
        return [ALL_FLOORS]
      }
      return [...(this.currentWorkPermit.floors || [])]
    },
    floorsHklOptions () {
      const protectedFloors = this.disableProtectedFloorRemoval
        ? new Set(this.protectedFloorLabels)
        : new Set()
      const toFloorOption = (f) => {
        const option = {
          name: f.label,
          value: f.label,
        }
        if (protectedFloors.has(f.label)) {
          option.removeDisabled = true
        }
        return option
      }
      const options = this.floorsOptions || []
      if (this.isTenantProject && options.length && options[0].floors) {
        return options.map((group) => ({
          name: group.title,
          options: (group.floors || []).map(toFloorOption),
        }))
      }
      return options.map(toFloorOption)
    },
    workTypeHklOptions () {
      return (this.descriptionOptionValue || []).map((wt) => ({ name: wt, value: wt }))
    },
    metaData () {
      return {
        isUploadedInAppr: this.currentWorkPermit.status === WORK_PERMIT_STATUS.APPR,
      }
    },
    defaultCompanyOptions () {
      if (!(this.isTenantProject && this.isStandalone)) return []
      return this.currentWorkPermit.tenancy.tenancyContractors
        .map((item) => ({
          name: item.company.name,
          id: item.company.id,
        }))
    },
  },
  methods: {
    ...mapMutations('workPermit', {
      updateFieldByName: mutationTypes.WORKPERMIT_SET_WORKPERMIT_FIELD_BY_NAME,
      updateHoistingValue: mutationTypes.WORKPERMIT_SET_HOISTING_ATTRIBUTE,
    }),
    ...mapActions('workPermit', ['createWorkPermit', 'changeWorkPermitStatus', 'getWorkPermitById', 'updateWorkPermitManager', 'updateWorkPermitByExtendDate']),
    ...mapActions('projectContractors', ['getProjectContractors']),
    ...mapActions('projects', ['loadProject']),
    canProcessCurrentWP (permType) {
      return this.isProjectAvailable && canProcessCurrentWP(permType)
    },
    checkIfInvalidClassByDate (validDate) {
      return !validDate ? 'error-highlight' : ''
    },
    gotoCreatePage (id) {
      if (this.isStandalone) {
        const toName = this.currentWorkPermit.isTenantProject ? 'tenantStandaloneWp' : 'vendorStandaloneWp'
        this.$router.push({
          name: toName,
          param: {id: id},
          query: {edit: 'Y'}, // Y: edit mode, N: view mode
        })
        return
      }
      let name = 'tenantCreateWorkPermit'
      let from = 'tenantProjectDetail'
      if (!this.isTenantProject) {
        name = 'vendorCreateWorkPermit'
        from = 'vendorProjectDetail'
      }
      navigation.toPage(
        name,
        { id },
        { from },
      )
    },
    async goToCreate () {
      const id = this.sourceId
      this.gotoCreatePage(id)
    },
    async openReAssignModal () {
      let perms = ''
      if (this.isWP) {
        perms = WP_ACTION_TYPE_BY_NEXT_STATUS_MAPPING[this.currentStatusRef]
      } else {
        if (this.currentWorkPermit.status === WORK_PERMIT_STATUS.ACKNOWLEDGED) {
          perms = WP_ACTION_TYPE.PPReview2
        }
        if (this.currentWorkPermit.status === WORK_PERMIT_STATUS.REVIEWED) {
          perms = WP_ACTION_TYPE.PPApprove
        }
      }
      await this.$refs[`reAssign_nextReivewerModal_${this.currentWorkPermit.id}`].openModal(perms, this.currentWorkPermit)
    },

    closeReAssginModal () {
      this.$refs.reAssignModal.close()
    },
    async reAssignReviewer (nextReviewer) {
      const nextReviewerId = lodash.get(nextReviewer, 'id')
      await this.updateWorkPermitManager({
        request: {
          managerId: nextReviewerId,
          nextReviewer,
        },
      })
      const status = this.currentWorkPermit.status
      const result = lodash.cloneDeep(this.currentWorkPermit)
      let emailType
      const isPP = result.type === WORK_PERMIT_TYPES.PROJECT
      const isWP = result.type === WORK_PERMIT_TYPES.WORK
      const shouldNextReviewerTM3 = isPP && status === WORK_PERMIT_STATUS.ACKNOWLEDGED
      const shouldNextApproverTM3 = isPP && status === WORK_PERMIT_STATUS.REVIEWED
      const shouldNextReviewerT3 = isWP && [WORK_PERMIT_STATUS.ACKNOWLEDGED, WORK_PERMIT_STATUS.REVIEWED].includes(status)
      const shouldNextApproverT3 = isWP && status === WORK_PERMIT_STATUS.REVIEWED2
      switch (true) {
        case shouldNextReviewerTM3:
          emailType = EmailTypes.workPermits.nextReviewerPP
          break
        case shouldNextApproverTM3:
          emailType = EmailTypes.workPermits.nextApprover
          break
        case shouldNextReviewerT3:
          emailType = EmailTypes.workPermits.nextReviewerWP
          break
        case shouldNextApproverT3:
          emailType = EmailTypes.workPermits.nextApproverWP
          break
      }
      await sendEmail(emailType, result)
    },
    updateFieldByPath ({path: name, newValue: value}) {
      return this.updateFieldByName({name, value})
    },
    updateField (name, value) {
      if (name === 'workType' && value !== this.workTypeList.TELECOM) {
        this.resetTelecomDetails()
      }
      this.updateFieldByName({name, value})
      if (name === 'workType') {
        if (!this.isAttachmentMandatoryByWorkType(value) || this.filesLength > 0) {
          this.validAttachmentDoc = true
        }
      }
    },
    isAttachmentMandatoryByWorkType (workType) {
      if (workType === this.workTypeList.TELECOM) return true

      return this.isEventAttachmentMandatoryEnabled && workType === this.workTypeList.EVENT
    },
    resetTelecomDetails () {
      this.updateFieldByName({name: 'telecomTbcNbfRoom', value: ''})
      this.updateFieldByName({name: 'telecomTelDuct', value: ''})
      this.updateFieldByName({name: 'telecomOtherLocations', value: ''})
    },
    formatDate (date, showTime = true) {
      return date ? dateUtil.formatDate(date, showTime) : '--'
    },
    getFloor () {
      return this.currentWorkPermit.isAllFloors
        ? ALL_FLOORS
        : formatFloors(this.currentWorkPermit, this.currentBuilding, this.buildingFloorZoneMap)
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.generalInfo')
      let noisyWorkingHoursValid = true
      let standaloneContactValid = true
      if (this.noisyWorkingHoursChecked) {
        noisyWorkingHoursValid = this.$refs.noisyWorkingHours.validateMandatory()
      }
      if (this.isStandalone) {
        standaloneContactValid = !lodash.get(this.currentWorkPermit, 'contactList', []).some(contact => (!contact.firstName || !contact.lastName || !contact.phone))
      }
      this.validDescription = (!this.isOthers && !this.isEvent) || !!this.currentWorkPermit.description
      this.validAttachmentDoc = !this.isAttachmentMandatory || this.filesLength > 0
      this.validStartDate = !!this.currentWorkPermit.startDate
      this.validEndDate = !!this.currentWorkPermit.endDate && new Date(this.currentWorkPermit.endDate) > new Date(this.currentWorkPermit.startDate)
      this.validFloor = !lodash.isEmpty(this.currentWorkPermit.floors)
      this.validContractor = !lodash.isEmpty(this.currentWorkPermit.contractor)
      if (!this.currentWorkPermit.isTenantProject) {
        this.validBuilding = !lodash.isEmpty(this.currentWorkPermit.buildingId)
      }
      this.validWorkType = !!this.currentWorkPermit.workType
      return fusionInputCheckUtil.isAllFieldsValid('.generalInfo') &&
        noisyWorkingHoursValid &&
        standaloneContactValid &&
        this.validStartDate &&
        this.validEndDate &&
        this.validBuilding &&
        this.validFloor &&
        this.validContractor &&
        this.validAttachmentDoc &&
        this.validWorkType &&
        this.validDescription
    },
    updateHoistings (name, value) {
      (this.currentWorkPermit.hoistings || []).forEach(hoisting =>
        this.updateHoistingValue({
          id: hoisting.id,
          name,
          value,
        }),
      )
    },
    onContractorSearch (query) {
      this.contractorSearchText = String(query || '').trim()
      this.searchCompanies({ name: query || '' })
    },
    onContractorInput (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      this.updateField('contractorId', '')
      this.updateField('contractor', name ? { name, type: companyType.PC } : null)
    },
    getTemporaryContractorOption () {
      const searchText = String(this.contractorSearchText || '').trim()
      if (!searchText) return null

      const normalizedSearchText = searchText.toLowerCase()
      const hasExisting = (this.companyOptions || []).some((name) =>
        String(name || '').trim().toLowerCase() === normalizedSearchText,
      )
      if (hasExisting) return null

      return {
        name: searchText,
        value: searchText,
        isSingleTaggableInputTag: true,
        hklSelectHint: this.$t('hint.addTempCompany'),
      }
    },
    async updateContractor (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.contractorSearchText = ''
        this.updateField('contractorId', '')
        this.updateField('contractor', null)
        this.updateHoistings('contractorId', '')
        this.updateHoistings('contractor', null)
        return
      }
      this.contractorSearchText = ''
      let selectedCompany = this.searchCompaniesResult.find(item => item.name === name)
      if (!selectedCompany) {
        selectedCompany = this.defaultCompanyOptions.find(item => item.name === name)
      }
      this.updateField('contractorId', selectedCompany ? selectedCompany.id : '')
      this.updateField('contractor', selectedCompany || { name, type: companyType.PC })
      this.updateHoistings('contractorId', this.currentWorkPermit.contractorId)
      this.updateHoistings('contractor', this.currentWorkPermit.contractor)
    },
    onBuildingHklChange (code) {
      const building = (this.buildingOptions || []).find((b) => b.code === code) || null
      this.updateBuilding(building)
    },
    onFloorsHklChange (newValues) {
      const values = lodash.uniq(newValues || [])
      if (this.disableProtectedFloorRemoval) {
        const protectedValues = new Set(this.protectedFloorLabels)
        const restoredValues = []
        const currentValues = this.currentWorkPermit.floors || []
        currentValues.forEach(value => {
          if (values.includes(value) || protectedValues.has(value)) {
            restoredValues.push(value)
          }
        })
        values.forEach(value => {
          if (value !== ALL_FLOORS && !restoredValues.includes(value)) {
            restoredValues.push(value)
          }
        })
        if (!values.includes(ALL_FLOORS) && !lodash.isEqual(values, restoredValues)) {
          this.updateField('isAllFloors', false)
          this.updateField('floors', restoredValues)
          return
        }
      }

      if (values.includes(ALL_FLOORS)) {
        this.updateField('isAllFloors', true)
        this.updateField('floors', lodash.get(this.selectedBuilding, 'floor', []) || [])
        return
      }
      this.updateField('isAllFloors', false)
      this.updateField('floors', values)
    },
    updateBuilding (val) {
      if (this.currentWorkPermit.buildingId !== lodash.get(val, 'id')) {
        this.updateField('floors', [])
        if (this.currentWorkPermit.isAllFloors) {
          this.updateField('isAllFloors', false)
        }
      }
      this.updateField('buildingId', val ? val.id : null)
      this.updateField('building', val)
      this.updateHoistings('buildingId', val ? val.id : null)
    },
    updateFloor (val, type = 'ADD') {
      let workPermitFloors = lodash.cloneDeep(this.currentWorkPermit.floors)
      if (type !== 'ADD') {
        if (val === ALL_FLOORS) {
          workPermitFloors.length = 0
        } else {
          lodash.remove(workPermitFloors, floor => floor === val)
        }
      } else {
        workPermitFloors.push(val)
      }
      const isAllFloors = workPermitFloors[0] === ALL_FLOORS
      this.updateField('isAllFloors', isAllFloors)
      if (isAllFloors) {
        this.updateField('floors', this.selectedBuilding.floor || [])
      } else {
        this.updateField('floors', workPermitFloors)
      }
    },
    updateSAPremises (val) {
      this.updateField('wpPremises', val)
    },
    updateLocation (val) {
      this.updateField('location', val)
    },
    onNoisyWorkingHourChecked (event) {
      const checked = event.target.checked
      if (!checked) {
        this.updateField('noisyWorkingHours', [])
      }
      this.updateField('noisyWorkingHoursChecked', checked)
    },
    getFilesList (files) {
      this.filesLength = files.length
      this.validAttachmentDoc = !this.isAttachmentMandatory || this.filesLength > 0
    },
    openRejectExtensionModal () {
      this.$refs.rejectExtensionModal.open()
    },
    openExtendModal () {
      this.extendDate = null
      this.$refs.extendWorkPermitModal.open()
    },
    openRequestExtendModal () {
      this.requestExtendDate = null
      this.$refs.requestExtendWorkPermitModal.open()
    },
    openConfirmExtendModal () {
      this.confirmExtendDate = null
      if (this.currentWorkPermit.requestExtendDate && this.currentWorkPermit.endDate <= this.currentWorkPermit.requestExtendDate) {
        this.confirmExtendDate = this.currentWorkPermit.requestExtendDate
      }
      this.$refs.confirmExtendWorkPermitModal.open()
    },
    closeExtendModal () {
      this.$refs.extendWorkPermitModal.close()
    },
    closeRequestExtendModal () {
      this.$refs.requestExtendWorkPermitModal.close()
    },
    closeConfirmExtendModal () {
      this.$refs.confirmExtendWorkPermitModal.close()
    },
    closeInsuranceNotCoverModal () {
      this.$refs.insuranceNotCoverModal.close()
    },
    async checkInsuranceDateThenRequestExtension () {
      this.checkInsuranceDate({continueCallback: this.requestExtendPermit,
        cancelCallback: this.cancelRequestExtend,
        date: this.requestExtendDate})
    },
    async checkInsuranceDateThenConfirmExtension () {
      const hasConflict = await getWPConflictStatus(this.currentWorkPermit.id, new Date(this.confirmExtendDate).getTime())
      if (!hasConflict) {
        this.checkInsuranceDate({continueCallback: this.confirmExtendPermit,
          cancelExtend: this.cancelConfirmExtend,
          date: this.confirmExtendDate})
        return
      }
      this.$refs.extensionConflictModal.open(() => {
        this.checkInsuranceDate({continueCallback: this.confirmExtendPermit,
          cancelExtend: this.cancelConfirmExtend,
          date: this.confirmExtendDate})
      }, [], () => {
        this.cancelConfirmExtend()
      })
    },
    async checkInsuranceDateThenHKLDExtendWorkpermit () {
      const hasConflict = await getWPConflictStatus(this.currentWorkPermit.id, this.extendDate)
      if (!hasConflict) {
        this.checkInsuranceDate({continueCallback: this.extendPermit,
          cancelCallback: this.cancelExtend,
          date: this.extendDate})
        return
      }
      this.$refs.extensionConflictModal.open(() => {
        this.checkInsuranceDate({continueCallback: this.extendPermit,
          cancelCallback: this.cancelExtend,
          date: this.extendDate})
      }, [], () => {
        this.cancelExtend()
      })
    },
    async checkInsuranceDate ({continueCallback, cancelCallback, date, hint = i18n.t('workPermit.extendInsuranceNotCoverHint')}) {
      if (this.isTenantProject) {
        const result = await insuranceAPI.get({projectId: this.project.id})
        const insurance = result.length ? result[0] : {}
        if (!this.insuranceIsCover({startTime: insurance.startDate, endTime: insurance.endDate, extendTime: date})) {
          this.checkInsuranceContinue = continueCallback
          this.checkInsuranceCancel = cancelCallback
          this.insuranceNotCoverHint = hint
          this.$refs.insuranceNotCoverModal.open()
          return
        }
      }
      if (continueCallback) await continueCallback.call(this)
    },
    async clickInsuranceContinue () {
      if (this.checkInsuranceContinue) await this.checkInsuranceContinue()
      this.closeInsuranceNotCoverModal()
      formUtils.resetDirty()
    },
    async clickInsuranceCancel () {
      if (this.checkInsuranceCancel) await this.checkInsuranceCancel()
      this.closeInsuranceNotCoverModal()
    },
    generateExtendRequest (date) {
      const request = {}
      if (!this.currentWorkPermit.extendDate) {
        request.extendDate = this.currentWorkPermit.endDate
      }
      request.endDate = date
      return request
    },
    async requestExtendPermit () {
      const request = this.generateExtendRequest(this.requestExtendDate)
      const extendResult = await this.updateWorkPermitByExtendDate({request, extendType: 'request'})
      await sendEmail(EmailTypes.workPermits.applyExtendWPToHkLandUser, extendResult)
      await sendEmail(EmailTypes.workPermits.applyExtendWPToContractor, extendResult)
      this.closeRequestExtendModal()
    },
    async confirmExtendPermit () {
      try {
        const request = this.generateExtendRequest(this.confirmExtendDate)
        const emailType = EmailTypes.workPermits.approveExtendWP
        const extendResult = await this.updateWorkPermitByExtendDate({request, extendType: 'confirm'})
        extendResult.requestExtendDate = extendResult.endDate
        await sendEmail(emailType, extendResult)
        this.closeConfirmExtendModal()
      } catch (err) {
        this.closeConfirmExtendModal()
      }
    },
    async extendPermit () {
      const request = this.generateExtendRequest(this.extendDate)
      await this.updateWorkPermitByExtendDate({request, extendType: 'HKLandExtend'})
      this.closeExtendModal()
    },
    cancelExtend () {
      this.closeExtendModal()
      formUtils.resetDirty()
    },
    insuranceIsCover ({startTime, endTime, extendTime}) {
      if (!startTime || !endTime) return false
      const endTimestamp = new Date(endTime).setHours(23, 59, 59, 999)
      const extendTimestamp = new Date(extendTime).getTime()
      return new Date(startTime).getTime() <= extendTimestamp && extendTimestamp <= endTimestamp
    },
    cancelRequestExtend () {
      this.closeRequestExtendModal()
      formUtils.resetDirty()
    },
    cancelConfirmExtend () {
      this.closeConfirmExtendModal()
      formUtils.resetDirty()
    },
    openResumeModal () {
      this.$refs.resumeWorkPermitModal.open()
    },
    closeResume () {
      this.$refs.resumeWorkPermitModal.close()
    },
    openSuspendModal () {
      this.$refs.suspendWorkPermitModal.open()
    },
    closeSuspendModal () {
      this.$refs.suspendWorkPermitModal.close()
      this.$refs.suspendDropzone.resetMessages()
    },
    async cleanAllFiles (backend) {
      if (!this.$refs.suspendAttachment) return

      await this.$refs.suspendAttachment.cleanAllFiles(backend)
    },
    getSuspendFileList (list) {
      this.currentSuspendDocs = list
    },
    async cleanUnsubmittedDocs () {
      if (this.currentSuspendDocs.length > 0 && this.randomId) {
        await this.cleanAllFiles(true)
      }
    },
    async suspendPermit () {
      this.suspendEffectiveDate = new Date()
      this.updateField('suspendEffectiveDate', this.suspendEffectiveDate)
      const suspendEffectiveDate = this.currentWorkPermit.suspendEffectiveDate

      // uploaded file's sourceId should based on each suspend action
      const sourceKey = await getKey()
      let files = []
      if (this.currentSuspendDocs.length > 0) {
        this.currentSuspendDocs.forEach((item) => {
          files.push({
            id: item.id,
            filename: item.filename,
            source: sourceKey,
          })
        })
      }
      let result = null
      const oldWorkPermits = lodash.cloneDeep(this.workPermitsInProject)
      try {
        result = await this.changeWorkPermitStatus({
          status: WORK_PERMIT_STATUS.SUSPENDED,
          historyDetail: { comments: this.suspendComments, suspendEffectiveDate, docSourceKey: sourceKey },
          suspendEffectiveDate,
          files,
        })
      } catch (err) {
        await this.cleanAllFiles(true)
        return
      }
      result.workPermitsInProject = []
      // add attachments for e-mail
      const docIds = this.currentSuspendDocs.map(val => { return val.id })
      Object.assign(result, {docIds})
      // Update Vuex to avoid sending e-mail to unsaved contractor info
      await this.reloadProjectEssentials()
      result.hoistings = result.hoistings.filter(hoisting => hoisting.status === WORK_PERMIT_STATUS.SUSPENDED)
      if (result.type === WORK_PERMIT_TYPES.WORK) {
        await sendEmail(EmailTypes.workPermits.suspendWP, result)
      } else if (result.isTenantProject && result.type === WORK_PERMIT_TYPES.PROJECT) {
        // Filter child work permits which will be suspended together with parent paremit, add them to result
        oldWorkPermits.forEach(wp => {
          delete wp.self.hoistings
          if (wp.self.type === WORK_PERMIT_TYPES.WORK &&
          this.statusCanSuspend.includes(wp.self.status)) {
            wp.hoistings = wp.hoistings.filter(hoisting => this.statusCanSuspend.includes(hoisting.status))
            result.workPermitsInProject.push(wp)
          }
        })
        await sendEmail(EmailTypes.workPermits.suspendPP, result)
      }

      this.cleanAllFiles(false)
      this.suspendEffectiveDate = null
      this.suspendComments = null
      this.closeSuspendModal()
      formUtils.resetDirty()
    },

    openCancelSuspendConfirmModal () {
      if (this.currentSuspendDocs.length > 0) {
        this.$refs.cancelSuspendConfrimModal.open(this.cancelSuspend)
      } else {
        this.cancelSuspend()
      }
    },
    async cancelSuspend () {
      this.suspendEffectiveDate = null
      await this.cleanUnsubmittedDocs()
      this.closeSuspendModal()
      formUtils.resetDirty()
    },
    async resumePermit () {
      const oldWorkPermits = lodash.cloneDeep(this.workPermitsInProject)
      const result = await this.changeWorkPermitStatus({ status: this.currentWorkPermit.statusBeforeSuspend, isResumeAction: true, historyDetail: { comments: i18n.t('workPermit.resumed'), isResume: true } })
      result.workPermitsInProject = []
      // Update Vuex to avoid sending e-mail to unsaved contractor info
      await this.reloadProjectEssentials()
      result.hoistings = result.hoistings.filter(hoisting => this.statusCanSuspend.includes(hoisting.status))
      if (result.type === WORK_PERMIT_TYPES.WORK) {
        await sendEmail(EmailTypes.workPermits.resumedWP, result)
      } else if (result.isTenantProject && result.type === WORK_PERMIT_TYPES.PROJECT) {
        // Filter child work permits which will be resumed together with parent paremit, add them to result
        oldWorkPermits.forEach(wp => {
          delete wp.self.hoistings
          if (wp.self.type === WORK_PERMIT_TYPES.WORK && wp.self.status === WORK_PERMIT_STATUS.SUSPENDED) {
            wp.hoistings = wp.hoistings.filter(hoisting => hoisting.status === WORK_PERMIT_STATUS.SUSPENDED)
            result.workPermitsInProject.push(wp)
          }
        })
        await sendEmail(EmailTypes.workPermits.resumedPP, result)
      }
      this.closeResume()
    },
    async reloadProjectEssentials () {
      if (this.isStandalone) return

      if (this.isTenantProject) {
        await this.getProjectContractors(this.project.id)
      } else {
        await this.loadProject(this.project.id)
      }
    },
    async searchCompanies (query) {
      query.type = companyType.C
      query.active = true
      let tempCompanies = await companiesApi.searchCompanies(query)
      this.searchCompaniesResult = tempCompanies.concat(this.defaultCompanyOptions.filter(item => item.name.includes(query.name)))
    },
    showEditPencilFunc (item, currentUser) {
      return currentUser.id === item.createdBy && this.canUploadUnderAppr && item.metaData.isUploadedInAppr === 'true'
    },
    showDeleteBtnFunc (item) {
      return this.currentWorkPermit.status === WORK_PERMIT_STATUS.APPR ? this.currentUser.id === item.createdBy && this.canUploadUnderAppr && item.metaData.isUploadedInAppr === 'true' : true
    },
  },
}
</script>

<style lang='scss'>
@import "~styles/newMultiselect";
</style>

<style lang='scss' scoped>
.generalInfo {
  $blockMargin: 15px;
  position: relative;
  .bold {
    font-weight: bold;
  }
  .gray_font {
    font-size: 12px;
    color: #878787;
    display: inline-block;
    height: 20px;
    margin-bottom: 5px;
  }
  .yellow_font {
    font-size: 14px;
    color: #E9B741;
    align-self: flex-end;
    margin-left: 5%;
  }
  .general-info-main {
    display:flex;
    .general-info-basic-left {
      flex: 9;
    }
    .work-permit-qr-code{
      margin-right: 60px;
      width: 250px;
    }
    .qr-margin-top{
      margin-top: 55px;
    }
  }
  .suspend-resume-extend-btns {
    text-align: right;
    .btns-group {
      margin-bottom: 10px;
    }
  }
  .suspend-resume-extend-btns.top-right-buttons {
    position: absolute;
    top: 0px;
    right: 0px;
  }
  .suspend-resume-extend-history {
    text-align: right;
  }
  p {
    margin-bottom: 0px;
  }

  .general-info__time {
    display: flex;
    align-items: flex-start;
  }
  .generalInfo_duration {
    margin-right: 20px;
  }
  .generalInfo_contractorName,
  .generalInfo_workPermitNo,
  .general-info__tenantTradeName,
  .general-info__time,
  .generalInfo_floor,
  .generalInfo_building,
  .generalInfo_location,
  .generalInfo_premises,
  .generalInfo_descriptionOfWork,
  .generalInfo_attachment,
  .generalInfo_noisyWorkingHours{
    margin-bottom: $blockMargin;
  }
  .generalInfo_telecom {
    margin-bottom: $blockMargin;
    &.readonly {
      margin-top: $blockMargin
    }
    > .row {
      @media (min-width: 40em) {
        justify-content: space-between;
      }
    }
    ::v-deep &-item {
      @media (min-width: 40em) {
        max-width: 30%;
      }
      label {
        margin-top: 0;
        white-space: nowrap;
      }
      input {
        width: 100%;
      }
    }
  }
  .generalInfo_contractorName > .gray_font {
    margin-top: 5px;
    margin-bottom: 0;
  }
  .generalInfo_location {
    input, .text-input textarea {
      width: 100% !important;
    }
  }
  .generalInfo_building {
    .seltiselect_tags-wrap {
      height: 38px;
    }
  }
  .generalInfo_floor.new-multiselect {
    ::v-deep .hkl-select,
    ::v-deep .general-info-floor-select {
      display: block;
      width: 100%;
    }
    ::v-deep .hkl-select .ant-select,
    ::v-deep .hkl-select.ant-select,
    ::v-deep .general-info-floor-select .ant-select {
      display: block;
      width: 100%;
    }
  }
  .generalInfo_descriptionOfWork ::v-deep {
    .edit-label {
      margin-bottom: 5px;
    }
    .hkl-select {
      margin-bottom: 15px;
    }
    textarea {
      width: 100% !important;
      min-height: 90px !important;
    }
  }
  .generalInfo_timePicker {
    display: flex;
    align-items: center;
    > .date-time-select {
      flex: 0 0 300px;
      max-width: 300px;
      width: 300px;
      html[lang='zh-CN'] & {
        flex-basis: 320px;
        max-width: 320px;
        width: 320px;
      }
    }
  }
  .generalInfo_timePickerTo {
    margin-right: 10px;
    margin-left: 5px;
  }
  .generalInfo_timePicker ::v-deep {
     .vdp-datepicker {
      vertical-align: bottom;
    }
    .date-time-select {
      display: inline-block;
      width: 300px;
      html[lang='zh-CN'] & {
        width: 320px;
      }
    }
    .vdp-datepicker, .hkl-select {
      display: inline-block;
    }
  }
  .error-highlight {
    background-color: #f9ecea;
  }
  ::v-deep .error-highlight {
    .hkl-select .ant-select-selection {
      background-color: #f9ecea !important;
    }
    .vue-dropzone {
      background-color: #f9ecea
    }
  }
  .gray_font {
    font-size: 12px;
    color: #878787;
    display: inline-block;
    height: 20px;
    margin-bottom: 5px;
  }
  .last-updator-info {
    font-style: italic;
    color: #6c6767;
    font-size: 12px;
  }
  .error-highlight input {
    background: #f9ecea;
  }
}
.extend__work-permit {
  .extend-permit-hint {
    font-weight: bold;
  }
}

.suspend__work-permit {
  .suspend-permit-hint {
    font-weight: bold;
  }
  .suspend-permit__remarks {
    margin-top: 10px;
    margin-bottom: 10px;
    .text-input textarea {
      width: 100%;
      min-height: 90px;
    }
  }
  .suspend-file_container{
    .filename {
      width: 90%;
    }
    .number {
      width: 5%;
    }
  }
}
::v-deep .fusion-input-check-is-invalid-input {
  border-color: #B5B5B5 !important;
}
</style>
